import { useState, useRef } from "react";

const T = {
  bg:"#0B0F1A", surface:"#111827", card:"#1A2235", border:"#1E2D45",
  accent:"#38BDF8", green:"#34D399", yellow:"#FCD34D", red:"#F87171",
  purple:"#A78BFA", orange:"#FB923C", textPri:"#F0F6FF", textSec:"#94A3B8",
  textMute:"#4B5563", codeBg:"#060D18", codeStr:"#86EFAC", codeKw:"#7DD3FC",
  codeFn:"#FDE68A", codeCmt:"#475569", codeNum:"#F9A8D4",
};

const AI = "#A78BFA"; // AI accent (purple)

// ── SMALL HELPERS ────────────────────────────────────────────────────────────

function H2({ children }) {
  return <h2 style={{fontSize:18,fontWeight:700,color:T.textPri,margin:"22px 0 10px",
    borderBottom:`1px solid ${T.border}`,paddingBottom:6}}>{children}</h2>;
}
function H3({ children }) {
  return <h3 style={{fontSize:15,fontWeight:700,color:AI,margin:"16px 0 8px"}}>{children}</h3>;
}
function P({ children, style={} }) {
  return <p style={{fontSize:13.5,color:T.textSec,lineHeight:1.8,margin:"0 0 12px",...style}}>{children}</p>;
}
function C({ children }) {
  return <code style={{background:"#0D1929",color:T.accent,padding:"1px 6px",borderRadius:4,
    fontFamily:"monospace",fontSize:12.5}}>{children}</code>;
}
function Li({ children }) {
  return <li style={{color:T.textSec,lineHeight:1.8,fontSize:13.5,marginBottom:4}}>{children}</li>;
}

function Story({ children }) {
  return (
    <div style={{background:"rgba(167,139,250,.08)",border:"1px solid rgba(167,139,250,.3)",
      borderLeft:"3px solid #A78BFA",borderRadius:8,padding:"12px 16px",marginBottom:14,
      fontSize:13.5,color:"#C4B5FD",lineHeight:1.8}}>
      <span style={{fontWeight:700,color:"#A78BFA",marginRight:6}}>🎈 The Story:</span>{children}
    </div>
  );
}
function Def({ children }) {
  return (
    <div style={{background:"rgba(56,189,248,.08)",border:"1px solid rgba(56,189,248,.3)",
      borderLeft:"3px solid #38BDF8",borderRadius:8,padding:"12px 16px",marginBottom:14,
      fontSize:13.5,color:T.textSec,lineHeight:1.8}}>
      <span style={{fontWeight:700,color:T.accent,marginRight:6}}>📖 Definition:</span>{children}
    </div>
  );
}
function Board({ children }) {
  return (
    <div style={{background:"#060D18",border:`1px solid ${T.border}`,borderLeft:`3px solid ${T.green}`,
      borderRadius:8,padding:"12px 16px",marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:700,letterSpacing:1.5,color:T.green,textTransform:"uppercase",marginBottom:8}}>✏️ On the Board</div>
      <pre style={{fontFamily:"'JetBrains Mono','Fira Mono',monospace",fontSize:12,
        color:T.textSec,lineHeight:1.65,margin:0,overflowX:"auto",whiteSpace:"pre"}}>{children}</pre>
    </div>
  );
}
function BrainCheck({ items }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{background:"rgba(252,211,77,.06)",border:"1px solid rgba(252,211,77,.25)",
      borderLeft:"3px solid #FCD34D",borderRadius:8,marginBottom:14,overflow:"hidden"}}>
      <button onClick={()=>setOpen(o=>!o)} style={{width:"100%",textAlign:"left",
        background:"none",border:"none",padding:"10px 14px",cursor:"pointer",
        display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span style={{fontSize:12,fontWeight:700,color:T.yellow,letterSpacing:.5}}>🎯 CHECK THEIR BRAIN</span>
        <span style={{color:T.textMute,fontSize:12}}>{open?"▾":"▸"}</span>
      </button>
      {open && (
        <div style={{padding:"0 14px 12px"}}>
          {items.map((item,i)=>(
            <div key={i} style={{marginBottom:12}}>
              <div style={{fontSize:13,color:T.textPri,fontWeight:600,marginBottom:4}}>Q{i+1}. {item.q}</div>
              {item.a && <div style={{fontSize:12.5,color:T.green,fontFamily:"monospace",lineHeight:1.6,
                paddingLeft:12,borderLeft:`2px solid ${T.green}33`}}>→ {item.a}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
function Note({ children, color=T.accent }) {
  return (
    <div style={{background:`${color}0D`,border:`1px solid ${color}30`,borderLeft:`3px solid ${color}`,
      borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:13,color:T.textSec,lineHeight:1.75}}>
      {children}
    </div>
  );
}
function DTable({ headers, rows }) {
  return (
    <div style={{overflowX:"auto",marginBottom:16}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
        <thead>
          <tr>{headers.map((h,i)=>(
            <th key={i} style={{background:"#0D1929",color:AI,padding:"8px 12px",textAlign:"left",
              borderBottom:`1px solid ${T.border}`,fontWeight:600,whiteSpace:"nowrap"}}>{h}</th>
          ))}</tr>
        </thead>
        <tbody>{rows.map((row,ri)=>(
          <tr key={ri} style={{borderBottom:`1px solid ${T.border}33`}}>
            {row.map((cell,ci)=>(
              <td key={ci} style={{padding:"8px 12px",color:T.textSec,lineHeight:1.65,verticalAlign:"top",
                background:ri%2===0?"transparent":"rgba(30,45,69,.3)"}}>
                {cell}
              </td>
            ))}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

// ── LESSON COMPONENTS ─────────────────────────────────────────────────────────

function L0() { return (
  <div>
    <Note color={T.orange}>⚠️ Teach this first. Zero background needed, students can try immediately, and every later concept — especially Lesson 8 — builds on it. A prompt is how you <em>program</em> an AI using ordinary language.</Note>

    <Story>
      "I give two students ₹100 each and send them to the shop.<br/><br/>
      To the first: <strong>'Go get something.'</strong> He comes back with chips. Not wrong… but not what I wanted.<br/><br/>
      To the second: <strong>'Get 1 litre of full-cream milk from the shop near the temple. If they don't have it, get toned milk instead. Bring the bill.'</strong><br/><br/>
      Neither student is smarter. The <strong>instruction</strong> was smarter. A prompt is what you say to an AI — and the quality of what you get back depends almost entirely on the quality of what you sent in."
    </Story>

    <Def>
      <strong>Prompt</strong> — the input instruction given to an AI model that tells it what to do.<br/>
      <strong>Prompt engineering</strong> — the skill of writing instructions that reliably produce the output you actually want.
    </Def>

    <H2>Bad vs Good Prompts</H2>
    <DTable
      headers={["❌ Bad Prompt","Why it fails","✅ Good Prompt"]}
      rows={[
        ['"Write about dogs."',"No length, no audience, no purpose",'"Write a 100-word paragraph about why dogs make good pets, for a Class 6 school magazine."'],
        ['"Fix my code."',"Doesn't say what's wrong or what language",'"This Python function should return the average but returns 0. Find the bug and explain it simply."'],
        ['"Tell me about history."',"Which history? Whose? When?",'"Explain the three main causes of the French Revolution to a 12-year-old, using one everyday example for each."'],
        ['"Make it better."','"Better" means nothing to a machine','"Rewrite this paragraph to be shorter, simpler, and in an encouraging tone."'],
        ['"Is this good?"',"No standard to judge against",'"Score this essay out of 10 on structure, grammar, and originality. Give one specific improvement for each."'],
      ]}
    />

    <H2>The Recipe — 5 Ingredients of a Good Prompt</H2>
    <DTable
      headers={["Ingredient","Question it answers","Example"]}
      rows={[
        [<strong style={{color:AI}}>Role</strong>,"Who should the AI be?",'"You are a patient science teacher."'],
        [<strong style={{color:T.green}}>Task</strong>,"What exactly must it do?",'"Explain photosynthesis."'],
        [<strong style={{color:T.accent}}>Context</strong>,"Who is it for / what's the situation?",'"For a Class 7 student who finds science boring."'],
        [<strong style={{color:T.yellow}}>Format</strong>,"What shape should the answer take?",'"In 5 bullet points, then one analogy."'],
        [<strong style={{color:T.orange}}>Constraints</strong>,"What are the limits and rules?",'"Under 150 words. No chemical equations."'],
      ]}
    />
    <Note color={T.yellow}>Rule of thumb for kids: <em>"If a new student in your class could follow your instruction without asking a single question, it's a good prompt."</em></Note>

    <Board>{`  VAGUE PROMPT  ──►  [ AI ]  ──►  vague, generic, unusable answer
  ROLE + TASK + CONTEXT + FORMAT + CONSTRAINTS
                ──►  [ AI ]  ──►  exactly what you needed`}</Board>

    <BrainCheck items={[
      {q:`Fix this prompt: "Help me with my project."`,a:"Any answer that adds subject, deadline, class level, and what kind of help."},
      {q:`Which ingredient is missing: "You are a chef. Give me a recipe for a 12-year-old to cook alone."`,a:"Format — how many steps, ingredient list or paragraph?"},
      {q:"Why does adding a Role change the answer at all?",a:"It sets vocabulary, tone, and assumptions — a chef and a doctor answer 'is this safe to eat?' very differently."},
    ]} />
  </div>
);}

function L1() { return (
  <div>
    <Story>
      "Imagine a big school called <strong>Artificial Intelligence School</strong>.<br/><br/>
      Inside sits a department called the <strong>Creative Arts Department</strong> — that's <strong>Generative AI</strong>. Kids there make new things: paintings, songs, stories, computer code.<br/><br/>
      Inside that department sits one student called <strong>Rohan</strong>. Rohan has read every book in the world's biggest library. His superpower? Give him half a sentence and he can guess the next word almost perfectly. Rohan is a <strong>Large Language Model</strong>.<br/><br/>
      Rohan is part of the Creative Arts Department. He is not the whole department."
    </Story>

    <Def>
      <strong>Artificial Intelligence (AI)</strong> — the whole field of making machines do things that normally need human intelligence.<br/><br/>
      <strong>Generative AI (GenAI)</strong> — any system that creates new content: text, images, audio, video, code. It often combines several kinds of media and higher-level reasoning.<br/><br/>
      <strong>Large Language Model (LLM)</strong> — a specific kind of neural network trained on huge amounts of text to predict the next token (roughly, the next word-piece). An LLM is one component that can power GenAI, but it is not the whole system.
    </Def>
    <Note color={T.green}><strong>One-line exam answer:</strong> GenAI is the broader concept. An LLM is one particular type of model used inside GenAI.</Note>

    <Board>{`┌───────────────────────────────────────────┐
│  ARTIFICIAL INTELLIGENCE                  │
│  ┌─────────────────────────────────────┐  │
│  │  GENERATIVE AI                      │  │
│  │  (makes NEW things)                 │  │
│  │   ┌──────────┐  ┌──────────┐        │  │
│  │   │   LLM    │  │  Image   │        │  │
│  │   │  (text)  │  │  Model   │  ...   │  │
│  │   └──────────┘  └──────────┘        │  │
│  └─────────────────────────────────────┘  │
└───────────────────────────────────────────┘`}</Board>

    <BrainCheck items={[
      {q:"Is every LLM a GenAI system? Is every GenAI system an LLM?",a:"Every LLM is GenAI — yes. Every GenAI is an LLM — NO. An image generator isn't an LLM."},
      {q:"A tool that turns your voice into a cartoon video — GenAI? LLM?",a:"GenAI yes, LLM no."},
    ]} />
  </div>
);}

function L2() { return (
  <div>
    <Story>
      "How do you know your friend is upset?<br/><br/>
      You <strong>hear</strong> his voice go quiet. You <strong>see</strong> his face. You <strong>read</strong> the message he sent you. Three 'modes' — sound, sight, text. That's <strong>multimodality</strong>.<br/><br/>
      An old AI was like a person who could only read. A multimodal AI can read, see, hear, and speak — all at once, in one brain."
    </Story>

    <Def>
      <strong>Multimodality</strong> — the ability of a system to process, understand, and generate information across multiple modalities (text, images, audio, video, sensor data) within a single system.
    </Def>

    <Board>{`   TEXT  ─┐
  IMAGE  ─┤
  AUDIO  ─┼──►  ONE MODEL  ──►  TEXT / IMAGE / AUDIO out
  VIDEO  ─┤
 SENSORS ─┘`}</Board>

    <BrainCheck items={[
      {q:'You send a photo of your maths homework and ask "where did I go wrong?" — how many modalities in? How many out?',a:"In: image + text = 2 modalities. Out: text = 1."},
    ]} />
  </div>
);}

function L3() { return (
  <div>
    <Note color={T.orange}>⚠️ Teach only the idea of attention here. No maths, no architecture diagram. The mechanics come in Lesson 4.</Note>

    <H2>Part A — The Old Way (why it was broken)</H2>
    <Story>
      "Remember <strong>Chinese Whispers</strong>? Twenty kids in a line. The first whispers 'The elephant ate a mango' to the second...<br/><br/>
      By the twentieth kid it's become 'An elegant aunt at a bungalow.'<br/><br/>
      That's how AI used to read sentences — one word at a time, passing a message down a line. By the end of a long paragraph, it had <strong>forgotten the beginning</strong>."
    </Story>

    <H2>Part B — The New Way (self-attention)</H2>
    <Story>
      "Now imagine all twenty kids sit in a <strong>circle</strong>. Everyone can look at everyone else at the same time.<br/><br/>
      Read this: <strong>'The trophy didn't fit in the suitcase because it was too big.'</strong><br/><br/>
      What does <em>'it'</em> mean — the trophy or the suitcase? The trophy. How did you know? Your brain instantly paid attention to 'trophy' and 'big' together — you looked at the whole sentence at once.<br/><br/>
      That linking is <strong>self-attention</strong>. And because everyone looks simultaneously, the computer does it in <strong>parallel</strong>. Fast, and it never forgets the start of a long text. That machine is the <strong>Transformer</strong>."
    </Story>
    <Note color={T.yellow}>Killer follow-up: change one word — "...because it was too <em>small</em>." Now 'it' means the suitcase. Same sentence, one word changed, attention shifts. Students find this genuinely surprising.</Note>

    <Def>
      <strong>Transformer</strong> — a neural-network architecture that processes sequences using self-attention, allowing parallel computation and capturing long-range dependencies.<br/><br/>
      • <em>self-attention</em> → every word looks at every other word to decide what matters<br/>
      • <em>parallel computation</em> → all at once, not one-by-one → fast<br/>
      • <em>long-range dependencies</em> → can connect word #1 to word #500 without forgetting
    </Def>
    <Note color={T.green}><strong>The link to remember:</strong> LLMs are <em>built on</em> the Transformer architecture. The Transformer is the engine; the LLM is the finished car.</Note>

    <Board>{`"The trophy didn't fit in the suitcase because IT was too big."
                                              ↑
                       thick arrow ←─────────┤─────────→ thick arrow
                          "trophy"           IT           "big"
        thin arrows to all other words — attention = the thickness`}</Board>

    <BrainCheck items={[
      {q:"Why is the circle better than the line?",a:"Nothing gets forgotten; parallel = faster."},
      {q:'"Riya gave Meera her book because she had lost it." Which words does \'she\' attend to?',a:"Genuinely ambiguous — even humans need more context. Great class discussion. Attention isn't magic."},
    ]} />
  </div>
);}

function L4() { return (
  <div>
    <Note color={T.orange}>⚠️ Teach as a factory conveyor belt. No equations. A sentence enters one end, a prediction comes out the other, passing through six machines.</Note>

    <Board>{` "The cat sat on the ___"
          │
          ▼
 ① TOKENISATION          →  cut into pieces
          ▼
 ② INPUT EMBEDDING       →  each piece becomes numbers (a vector)
          ▼
 ③ POSITIONAL ENCODING   →  stamp a seat number on each piece
          ▼
 ④ MULTI-HEAD ATTENTION  →  every piece looks at every other piece
          ▼               (repeat ④ and ⑤ many times)
 ⑤ FEED-FORWARD NETWORK  →  each piece thinks on its own
          ▼
 ⑥ OUTPUT LAYER          →  scores for every possible next word
          ▼
       "mat" (87%)`}</Board>

    <H3>① Tokenisation</H3>
    <Story>"You can't eat a whole roti in one bite. You tear it into pieces first. The computer does the same to a sentence."</Story>
    <Board>{`"unbelievable"  →  [ un ][ believ ][ able ]
"The cat sat"   →  [ The ][ cat ][ sat ]`}</Board>
    <Def><strong>Tokenisation</strong> — breaking raw text into smaller units called tokens (words, sub-words, or characters), which are the basic units the model actually processes.</Def>
    <Note color={T.yellow}>Why sub-words, not whole words? If I invent "unfriendable" — with whole words the AI is stuck. With pieces (un + friend + able) it can still make sense of it. Roughly 1 token ≈ ¾ of an English word. This is also what you're billed for when using an AI API.</Note>

    <H3>② Input Embedding & Vector Embeddings</H3>
    <Story>"A computer cannot read the word 'cat'. It only understands numbers. So we give every token a set of coordinates — like a location on a map. And on this map, <strong>words with similar meanings are placed near each other</strong>. 'Cat' sits next to 'kitten'. 'King' sits far from 'banana'."</Story>
    <Board>{`   cat    →  [0.21, -0.88, 0.43, ... ]   ← hundreds of numbers
   kitten →  [0.19, -0.85, 0.41, ... ]   ← very close to cat
   banana →  [-0.77, 0.32, -0.64, ...]   ← far away`}</Board>
    <Def>
      <strong>Input embedding</strong> — converting raw input tokens into continuous vector representations, forming the first layer of a transformer model.<br/><br/>
      <strong>Vector embedding</strong> — a continuous numeric representation that maps discrete tokens into dense vectors in high-dimensional space, so the transformer can process them.
    </Def>
    <Note color={T.accent}>Remember this term. Vector embeddings come back in Lesson 5 — they are how RAG finds the right document. Same idea, different job.</Note>

    <H3>③ Positional Encoding</H3>
    <Story>"The transformer reads all words at the same time — fast, but there's a problem.<br/><br/><strong>'Dog bites man'</strong> vs <strong>'Man bites dog'</strong><br/><br/>Same three words. Completely different news story! So we stamp a <strong>seat number</strong> on every token before it enters. Word 1, word 2, word 3. Now the order is preserved even though everything is read simultaneously."</Story>
    <Def><strong>Positional encoding</strong> — adds a representation of token order to the model, by adding a positional vector to each token's embedding, so the transformer can distinguish positions in the sequence.</Def>

    <H3>④ Multi-Head Self-Attention</H3>
    <Story>"Six teachers read the same student essay.<br/><br/>The English teacher looks at grammar. The history teacher checks the facts. The counsellor notices the mood. Each one sees something the others missed. Then they combine their notes into one report.<br/><br/>That's <strong>multi-head</strong> attention. Each 'head' is one teacher, looking at the same sentence for a different kind of relationship. All at the same time. Then the results are merged."</Story>
    <Def><strong>Multi-head self-attention</strong> — a mechanism that lets each token attend to every other token using several parallel attention heads, enabling the model to capture diverse contextual relationships and produce richer representations.</Def>
    <Note color={T.yellow}>Why "multi"? One head tracks one kind of relationship well. Eight+ heads let the model track several patterns at once. Ask: "Why not use 1000 heads?" — Answer: cost and diminishing returns.</Note>

    <H3>⑤ Feed-Forward Neural Network</H3>
    <Story>"After the group discussion (attention), every student goes back to their own desk and thinks alone about what they just heard. No talking to neighbours. Information moves in one direction only — in, think, out."</Story>
    <Def><strong>Feed-forward neural network</strong> — a network in which information flows only forward, from input through hidden layers to output, with no recurrent or cyclic connections.</Def>
    <Note>Steps ④ and ⑤ repeat as a pair, many times over — often dozens of layers. Discuss → think alone → discuss → think alone. This stacking is where "large" in Large Language Model largely comes from.</Note>

    <H3>⑥ Output Layer</H3>
    <Story>"Finally the machine gives every word in its dictionary a score. 'mat' — 87. 'floor' — 6. 'sofa' — 4. 'banana' — 0.001. Then it turns those scores into percentages and picks one."</Story>
    <Def><strong>Output layer</strong> — the final layer that projects hidden states to output logits (a linear layer mapping to vocabulary size), often followed by <strong>softmax</strong> to produce probabilities.</Def>
    <Note color={T.red}><strong>The honest punchline — say this out loud:</strong> "After all six machines, all that comes out is a <strong>guess at the next word</strong>. That's it. Then it adds that word to the sentence and runs the whole factory again. Everything an LLM writes is that one trick, repeated." Students find this both deflating and clarifying.</Note>

    <BrainCheck items={[
      {q:"Why can't we skip tokenisation?",a:"The model needs discrete units; it can't process raw characters as meaning."},
      {q:"What breaks if we remove positional encoding?",a:'"Dog bites man" becomes identical to "Man bites dog".'},
      {q:"Why is it called self-attention?",a:"The sentence attends to itself — not to some other external input."},
      {q:"Which two steps repeat many times?",a:"Attention (④) and feed-forward (⑤)."},
      {q:"If the output is just the next word, how do we get a whole paragraph?",a:"Run it again and again, feeding each new word back in."},
    ]} />
  </div>
);}

function L5() { return (
  <div>
    <Story>
      "Two exams tomorrow.<br/><br/>
      <strong>Exam A is closed-book.</strong> You walk in with nothing. Everything comes from inside your head. Fine for what you studied — but if the question is about something that happened last week, you're stuck. And when unsure, you might confidently write something wrong.<br/><br/>
      <strong>Exam B is open-book.</strong> Before answering, you first go and find the right page, read it, then write.<br/><br/>
      A plain <strong>LLM</strong> is the closed-book student. <strong>RAG</strong> is the open-book student."
    </Story>

    <Def><strong>RAG (Retrieval-Augmented Generation)</strong> — retrieves relevant external documents before generating a response. A plain LLM generates text solely from its internal parameters, with no retrieval step.</Def>

    <Board>{`Plain LLM:  Question ─────────────────────────────► Answer
                       (from memory only)
RAG:        Question ──► SEARCH ──► found text + question ──► Answer`}</Board>

    <H2>The Library Analogy</H2>
    <DTable
      headers={["In the library","In RAG","What actually happens"]}
      rows={[
        ["📚 Books","Documents","The knowledge you want to search — PDFs, notes, web pages, company files"],
        ["👩‍🏫 The librarian","Embedding model","She reads a book and decides what it's about, filing it by meaning — exactly what an embedding model does"],
        ["🔢 Shelf number","Vector embedding","A short numeric code that points to where the meaning lives"],
        ["🗺️ The library map","Vector space","The organised space where related books sit near each other"],
        ["🚶 Finding nearby books","Semantic search","You don't need the exact title — books about the same idea are physically close"],
        ["🗣️ Explaining the answer","The LLM","Reads the found pages and gives you a clear answer in plain language"],
      ]}
    />
    <Note color={AI}><strong>The key insight:</strong> The librarian doesn't file books alphabetically by title. She files them by <strong>meaning</strong>. That's why you can ask "why do mountains explode?" and still be handed the volcano book — even though you never said 'volcano'. That is the difference between keyword search and semantic search. And it works because of <strong>vector embeddings</strong> — the same idea from Lesson 4, step ②.</Note>

    <H2>The Five Components of RAG</H2>
    <DTable
      headers={["Component","Job","Library version"]}
      rows={[
        [<strong>Knowledge base</strong>,"Where all documents are stored","The library building"],
        [<strong>Retrieval module</strong>,"Fetches relevant documents","The librarian who walks to the shelf"],
        [<strong>Retrieval strategy</strong>,"How it decides what's relevant — how many docs, how ranked","The librarian's method: 'top 5 closest, published after 2020'"],
        [<strong>Fusion mechanism</strong>,"Combines retrieved text with the user's question","Placing the open books next to your question sheet"],
        [<strong>Generation module</strong>,"Writes the final answer","The person who reads it all and explains it to you"],
      ]}
    />

    <H2>Vertex AI RAG Engine</H2>
    <Def><strong>Vertex AI RAG Engine</strong> — a Google Cloud service that combines retrieval of relevant documents with generative AI to produce context-aware responses.</Def>
    <Story>"Building a library from scratch — buying shelves, hiring a librarian, making the map — takes months. Vertex AI RAG Engine is a <strong>ready-made library</strong> you can rent. You just bring your books."</Story>

    <H2>Why RAG Matters</H2>
    <ul style={{paddingLeft:20,marginBottom:16}}>
      <Li>The LLM's memory is <strong style={{color:T.textPri}}>frozen</strong> on the day training ended. RAG supplies today's information.</Li>
      <Li>The LLM has never read <em>your</em> school's rulebook. RAG can.</Li>
      <Li>RAG can <strong style={{color:T.textPri}}>show its source</strong> — so you can check whether the answer is true.</Li>
    </ul>

    <BrainCheck items={[
      {q:'Which can answer "what\'s the fee structure at our school?" — and why?',a:"RAG — that document was never in training data."},
      {q:"Does RAG make the LLM smarter, or better informed?",a:"Better informed. Same brain, better notes."},
      {q:"Which RAG component would you change if answers are relevant but outdated?",a:"Retrieval strategy — add a date filter."},
      {q:"Which two lessons both used vector embeddings?",a:"Lesson 4 (inside the transformer) and Lesson 5 (semantic search in RAG)."},
    ]} />
  </div>
);}

function L6() { return (
  <div>
    <Story>
      "Two helpers. Which is more useful?<br/><br/>
      <strong>Helper 1</strong> — You say: 'It's raining, what should I do?' He says: 'You should close the windows.' Then he stands there. He only talks.<br/><br/>
      <strong>Helper 2</strong> — Same question. He looks out of the window, checks if it's really raining, walks over, and <strong>closes the windows</strong>. Then tells you it's done.<br/><br/>
      Helper 1 is a language model. Helper 2 is an <strong>agent</strong>. The difference isn't intelligence. It's <strong>hands</strong>."
    </Story>

    <Def>
      <strong>Agent</strong> — an autonomous entity that perceives its environment and takes actions to achieve goals.<br/><br/>
      Underline three words: <em>perceives</em> (senses), <em>actions</em> (does), <em>goals</em> (has a purpose).
    </Def>

    <H2>The Five Properties of an Agent</H2>
    <ol style={{paddingLeft:20,marginBottom:16}}>
      <Li>Acts <strong style={{color:T.textPri}}>on behalf of a user</strong></Li>
      <Li>Possesses <strong style={{color:T.textPri}}>autonomy</strong> and decision-making capability</Li>
      <Li><strong style={{color:T.textPri}}>Interacts with its environment</strong> to achieve goals</Li>
      <Li>Can <strong style={{color:T.textPri}}>learn and adapt</strong> from experience</Li>
      <Li>Examples: virtual assistants, autonomous robots, self-driving cars</Li>
    </ol>

    <H2>How LLM, GenAI and Agent connect</H2>
    <Story>
      "Think of a <strong>robot delivery boy</strong>:<br/>
      • His <em>brain</em> that understands 'deliver this to Flat 402' → the <strong>LLM</strong><br/>
      • His ability to write the delivery note or draw a map → <strong>GenAI</strong><br/>
      • His <em>legs, hands, and decisions</em> → what makes him an <strong>Agent</strong>"
    </Story>
    <Note color={AI}>An AI agent uses an LLM as its core reasoning engine, while GenAI components let it produce text, images, or code. The agent routes user input and environment observations to the LLM, then executes actions based on the result.</Note>

    <BrainCheck items={[
      {q:"A calculator app — agent?",a:"No. No environment, no goal. It just computes."},
      {q:"A self-driving car?",a:"Yes — perceives with cameras, acts with steering, goal = reach destination."},
    ]} />
  </div>
);}

function L7() { return (
  <div>
    <Note color={T.orange}>⚠️ Students confuse these constantly. Use the same example for both so only the behaviour changes.</Note>

    <Story>
      "You tell two students: <strong>'Make the class notice board look nice for Diwali.'</strong><br/><br/>
      <strong>Student A</strong> asks: 'What should I stick? Where? Which colour?' You must specify every step. He does each perfectly. He is an <strong>AI Agent</strong>.<br/><br/>
      <strong>Student B</strong> says nothing. She decides a theme, buys the paper, plans a layout, realises the glue is finished, borrows some, finishes, and reports: 'Done — and I've saved leftover paper for Holi.'<br/><br/>
      She set her own sub-goals. She adapted when the glue ran out. She learned for next time. That's <strong>Agentic AI</strong>."
    </Story>

    <DTable
      headers={["","AI Agents","Agentic AI"]}
      rows={[
        ["What it is","Software components that execute specific tasks or respond to inputs","Systems endowed with true agency"],
        ["How it decides","Often follows predefined rules or models","Autonomous decision-making, sets its own goals"],
        ["Over time","Does the same thing the same way","Adapts and learns"],
        ["Analogy","An obedient worker","A responsible team member"],
      ]}
    />
    <Note color={T.green}><strong>One-line answer:</strong> AI Agents <em>execute</em>. Agentic AI <em>decides</em>.</Note>

    <BrainCheck items={[
      {q:"A thermostat that switches on the AC at 26°C?",a:"Agent. Fixed rule."},
      {q:"A system that notices you're always cold in the morning, changes its own schedule, and orders a service when the filter is dirty?",a:"Agentic — it sets its own sub-goals and adapts."},
    ]} />
  </div>
);}

function L8() { return (
  <div>
    <Note color={AI}>💡 Connect back to Lesson 0. A persona is a carefully written prompt that stays switched on permanently.</Note>

    <Story>
      "In a school play, before an actor says a single line, someone writes:<br/><br/>
      <em>'You are Dr. Sharma. You are 50, calm, and speak gently. You never shout. You carry a stethoscope. You are a doctor — so you do not give legal advice.'</em><br/><br/>
      Now the actor knows exactly how to behave in every scene, even ones nobody wrote lines for.<br/><br/>
      An <strong>agent persona</strong> is that character sheet."
    </Story>

    <Def><strong>Agent personas</strong> — archetypal profiles that define an AI agent's goals, motivations, and typical behaviours, used to guide design and interaction.</Def>

    <H2>The Five Pieces of a Persona</H2>
    <DTable
      headers={["Piece","The question it answers","Example: 'Vidya', school library assistant"]}
      rows={[
        [<strong style={{color:AI}}>Identity</strong>,"Who am I?",'"I am Vidya, the library assistant for Class 6–8 students."'],
        [<strong style={{color:T.green}}>Role</strong>,"What is my job?",'"Help students find books, check availability, and suggest what to read next."'],
        [<strong style={{color:T.accent}}>Behaviour</strong>,"How do I act and speak?",'"Friendly, patient, short answers, simple words. Never sarcastic. Always ask what they enjoyed reading last."'],
        [<strong style={{color:T.yellow}}>Tools</strong>,"What can I use?","Library catalogue search, borrowing records, due-date calendar."],
        [<strong style={{color:T.red}}>Boundaries</strong>,"What must I NEVER do?",'"Never share another student\'s borrowing history. Never help with homework answers. If asked something outside the library, politely redirect to a teacher."'],
      ]}
    />
    <Note color={T.red}><strong>Boundaries are the most important row and the one students skip.</strong> Ask them: "What could go wrong if Vidya had no boundaries?" — She'd reveal private records, do homework for people, and confidently answer medical questions. <em>A persona without boundaries is not a persona, it's a liability.</em></Note>

    <Board>{`        ┌──────────── AGENT PERSONA ────────────┐
        │                                       │
        │   IDENTITY  →  who am I               │
        │   ROLE      →  what is my job         │
        │   BEHAVIOUR →  how do I act           │
        │   TOOLS     →  what can I use         │
        │   BOUNDARIES→  what must I NEVER do   │
        │                                       │
        └───────────────────────────────────────┘`}</Board>

    <H2>Why Personas Exist</H2>
    <DTable
      headers={["Reason","What it does"]}
      rows={[
        ["Consistency","The agent behaves the same on Monday and Friday, with every user"],
        ["Safety","Boundaries stop it from doing damage"],
        ["Trust","Users know what to expect"],
        ["Design clarity","The team agrees on what it is before writing code"],
      ]}
    />

    <BrainCheck items={[
      {q:'Same question — "I have a headache" — asked to a doctor persona and a librarian persona. How do the answers differ?',a:"Doctor: advice. Librarian: 'I can only help with books — please see the school nurse.' The boundary does the work."},
      {q:'"You are Coach Raj. You help students plan their fitness routine. You are energetic and encouraging." Which piece is missing?',a:"Tools AND Boundaries — and the missing boundary is dangerous: he might give medical or diet advice he shouldn't."},
    ]} />
  </div>
);}

function L9() { return (
  <div>
    <H2>Tools</H2>
    <Story>
      "Open your school bag. What's inside?<br/>
      A calculator — for sums too hard to do in your head.<br/>
      A dictionary — for words you don't know.<br/>
      A phone — to ask someone else.<br/>
      A notebook — to remember things.<br/><br/>
      You are smart. But you're <em>much</em> more useful <strong>with the bag</strong>.<br/><br/>
      An agent's tools are that bag. The LLM is smart — but with tools it can search the internet, do real maths, read a file, send an email, book a ticket. <strong>Tools are how the agent's brain reaches the real world.</strong>"
    </Story>

    <Def><strong>Tools</strong> — reusable components an agent can use to sense, reason, or act.</Def>

    <DTable
      headers={["Tool","What it's for","Kid version"]}
      rows={[
        ["LLM","Natural language understanding","The brain"],
        ["Memory module","Store & retrieve past interactions","The diary"],
        ["Planner","Generate action sequences","The to-do list maker"],
        ["Executor","Carry out actions in the environment","The hands"],
        ["Dialogue manager","Manage conversation flow","The manners"],
        ["Knowledge base","Domain-specific facts","The textbook"],
        ["API connector","Reach external services","The phone"],
        ["Safety guardrails","Detect harmful content","The seatbelt"],
        ["Logging & monitoring","Debugging and analysis","The CCTV"],
      ]}
    />
    <Note color={T.orange}>⚠️ "Tools" gets used two ways: (1) Tools for building agents (frameworks, debuggers) — a developer's toolkit. (2) Tools an agent calls at runtime (search, calculator, send_email) — the agent's toolkit. In ADK, "tool" almost always means sense 2.</Note>

    <H2>The Five-Step Work Cycle</H2>
    <Story>
      "You're hungry. Watch your own brain work:<br/><br/>
      1. <strong>Reasoning</strong> — 'I'm hungry. Options: make Maggi, order food, eat fruit.'<br/>
      2. <strong>Selection</strong> — 'Maggi. Fastest, and Mummy said no ordering.'<br/>
      3. <strong>Invocation</strong> — You actually go and cook it.<br/>
      4. <strong>Observation</strong> — You taste it. Too much water. Not great.<br/>
      5. <strong>Finalisation</strong> — Note for next time: less water. Cycle done. Still hungry?<br/><br/>
      Every agent runs this exact loop, over and over."
    </Story>

    <DTable
      headers={["Step","Definition"]}
      rows={[
        [<strong style={{color:AI}}>Reasoning</strong>,"Analyse data and generate possible actions"],
        [<strong style={{color:T.green}}>Selection</strong>,"Choose the most suitable action from the options"],
        [<strong style={{color:T.yellow}}>Invocation</strong>,"Execute the chosen action"],
        [<strong style={{color:T.orange}}>Observation</strong>,"Monitor the outcome and gather feedback"],
        [<strong style={{color:T.accent}}>Finalisation</strong>,"Update state, learn from the result, prepare for the next cycle"],
      ]}
    />

    <Board>{`        ┌──► REASONING ──► SELECTION ──┐
        │                              ▼
  FINALISATION ◄── OBSERVATION ◄── INVOCATION

  ↑ Draw as a CIRCLE — the loop is what makes it an agent ↑`}</Board>
    <Note color={T.yellow}>The arrow from Finalisation must loop back to Reasoning. That loop is what makes it an agent instead of a one-shot answer.</Note>

    <BrainCheck items={[
      {q:"Which step does a plain chatbot skip entirely?",a:"Invocation and Observation — it never acts, so it never sees results."},
      {q:"What breaks if you remove Observation?",a:"The agent never knows if it succeeded. It repeats mistakes forever."},
    ]} />
  </div>
);}

function L10() { return (
  <div>
    <Story>
      "You want to build a toy car. Two options.<br/><br/>
      <strong>Option 1:</strong> Cut the wood, carve the wheels, make the axle from scratch. Weeks of work.<br/><br/>
      <strong>Option 2:</strong> Open a <strong>LEGO Technic box</strong>. Wheels, axles, motors, instruction booklet — all ready. An hour.<br/><br/>
      <strong>ADK is the LEGO box for building AI agents.</strong> Google made it, but the box works with any engine — a Gemini motor, or a different company's motor. It's <strong>model-agnostic</strong>."
    </Story>

    <Def>
      <strong>ADK = Agent Development Kit.</strong> A framework for building, running, and deploying agents. <strong>Model-agnostic</strong> — not restricted to Gemini; any model can be attached.
    </Def>

    <Board>{`pip install google-adk`}</Board>

    <H2>The Four Pieces of the Kit</H2>
    <DTable
      headers={["Piece","What it does","Kid version"]}
      rows={[
        [<strong>Agent SDK</strong>,"The code you write to define your agent","The LEGO bricks"],
        [<strong>Agent Runtime</strong>,"The engine that actually runs the agent","The motor"],
        [<strong>Agent API</strong>,"How other programs talk to your agent","The doorbell"],
        [<strong>Agent UI</strong>,"The screen where humans chat with it","The remote control"],
      ]}
    />
    <Note color={T.orange}>⚠️ These aren't four different ADKs — they are the <strong>four components of one ADK</strong>. Kit, not kits.</Note>

    <H2>BaseAgent — the Foundation</H2>
    <Story>"Every LEGO build starts on a <strong>flat green baseplate</strong>. House, car, or spaceship — same baseplate underneath. <strong>BaseAgent is that baseplate.</strong>"</Story>
    <Def><strong>BaseAgent</strong> — the foundational class that defines the core interface and behaviour for all agents in ADK. Everything else inherits from it.</Def>

    <H2>The Agent Family Tree</H2>
    <Board>{`                    BaseAgent
                        │
      ┌─────────────────┼─────────────────┐
      │                 │                 │
  LLM Agent      Workflow Agent      Custom Agent
                        │
        ┌───────────────┼───────────────┐
        │               │               │
   Sequential       Parallel          Loop`}</Board>

    <H3>1. LLM Agent</H3>
    <Def><strong>LLM Agent</strong> — an agent powered by a large language model, capable of reasoning, planning, and acting. The "thinking" agent — it decides what to do.</Def>

    <H3>2. Workflow Agent (Sequential, Parallel, Loop)</H3>
    <Def><strong>Workflow Agent</strong> — coordinates and executes a sequence of tasks or steps. This one doesn't think much; it organises.</Def>
    <DTable
      headers={["Type","Definition","🎈 Kid analogy"]}
      rows={[
        [<strong style={{color:T.green}}>Sequential</strong>,"Performs tasks in a specific, ordered sequence","Making tea: Boil → add tea → add milk → strain. You can't strain before you boil."],
        [<strong style={{color:AI}}>Parallel</strong>,"Executes multiple tasks concurrently","Mum cooking dinner: rice on one burner, dal on the other — at the same time. Neither waits."],
        [<strong style={{color:T.yellow}}>Loop</strong>,"Repeats tasks until a termination condition is met","Practising a song: Sing → is it right? No → sing again. Yes → stop."],
      ]}
    />
    <Note color={T.red}><strong>Stress this about Loop:</strong> it must have a stopping condition. "What if I never decide the song is good enough?" — You sing forever. That's an infinite loop — a real and expensive bug.</Note>

    <H3>3. Custom Agent</H3>
    <Def><strong>Custom Agent</strong> — a user-defined agent created by extending BaseAgent. When no ready-made type fits, build your own on the baseplate.</Def>

    <BrainCheck items={[
      {q:"Agent that: gets a news article → summarises → translates → emails it. Which type?",a:"Sequential — each step needs the previous output."},
      {q:"Check weather in 5 cities at once?",a:"Parallel — none depends on the others."},
      {q:"Rewrite an essay until it scores above 8/10?",a:"Loop."},
      {q:"Why does BaseAgent matter if you never use it directly?",a:"It guarantees every agent shares the same interface, so ADK can run any of them identically."},
    ]} />
  </div>
);}

function L11() { return (
  <div>
    <H2>Classroom Activity — "The Human Agent" (20 min, no computers)</H2>
    <Note color={AI}>Best activity in this document. Run it after Lesson 9. Five students, each with a paper label. Goal on board: "Find out if it will rain today, and tell the class what to carry."</Note>
    <DTable
      headers={["Student","Label","What they say/do"]}
      rows={[
        ["1","REASONING",'"I could look outside, check a weather app, or ask a teacher."'],
        ["2","SELECTION",'"Weather app is most reliable. Choose that."'],
        ["3","INVOCATION","Physically walks to the window / pretends to open the app."],
        ["4","OBSERVATION",'"It says 80% rain at 4 PM."'],
        ["5","FINALISATION",'"Conclusion: carry umbrellas. Noted for the class."'],
      ]}
    />
    <H3>Then Break It Deliberately</H3>
    <ul style={{paddingLeft:20,marginBottom:16}}>
      <Li><strong>Remove Student 4 (Observation)</strong> — the agent acts blindly and never knows if it worked.</Li>
      <Li><strong>Give Student 3 no tools</strong> — reasoning is useless without the ability to act.</Li>
      <Li><strong>Add a persona boundary to Student 1:</strong> "You may not go outside the classroom." Watch how the plan changes. (Links Lesson 8 to Lesson 9 perfectly.)</Li>
      <Li><strong>Run it as a Loop:</strong> send it back to Reasoning if the answer was unclear.</Li>
    </ul>

    <H2>Common Student Confusions — and the Fix</H2>
    <DTable
      headers={["They say","The fix"]}
      rows={[
        ['"LLM and GenAI are the same"',"The Rohan story. Rohan is in the Creative Arts Department, he is not the department."],
        ['"ChatGPT is an agent"',"Only if it can act. Pure chat = not an agent. Chat + tools + a loop = agent."],
        ['"Agentic AI is just a fancier AI agent"',"Not about being smarter — about who decides the steps."],
        ['"RAG is a type of model"',"No. RAG is a technique — a search step bolted in front of the model."],
        ['"Transformers are the same as LLMs"',"Transformer = architecture (engine). LLM = a trained model built with it (car)."],
        ['"A token is a word"',"Usually a piece of a word. 'unbelievable' is three tokens."],
        ['"Embeddings and encodings are the same"',"Embedding = what the word means. Positional encoding = where the word sits. Two different jobs."],
        ['"Parallel is just faster Sequential"',"No — Parallel only works when steps don't depend on each other. You can't parallelise making tea."],
        ['"A persona is just a name"',"A name with no boundaries is decoration. The Boundaries row is what makes it real."],
        ['"The four ADK parts are four different ADKs"',"Four components of one kit."],
      ]}
    />

    <H2>Board Summary — Leave This Up for the Whole Unit</H2>
    <Board>{`AI  ⊃  GenAI  ⊃  LLM  ─built on─►  TRANSFORMER (self-attention)
TRANSFORMER PIPELINE:
  tokenise → embed → position → attention → feed-forward → output
LLM + RETRIEVAL  =  RAG      (open-book instead of closed-book)
LLM + TOOLS + LOOP  =  AGENT
AGENT + PERSONA  =  predictable, safe agent
AGENT + AUTONOMY =  AGENTIC AI
ADK  =  the kit for building them`}</Board>

    <H2>Homework Ideas</H2>
    <DTable
      headers={["Level","Task"]}
      rows={[
        [<span style={{color:T.green}}>Easy</span>,"Find three agents in your own home. For each, write what it perceives and what action it takes."],
        [<span style={{color:T.green}}>Easy</span>,"Collect five bad prompts (your own or a friend's), and rewrite each using the five ingredients."],
        [<span style={{color:T.yellow}}>Medium</span>,"Draw the agent family tree from memory, then write one real-world job for each of the six agent types."],
        [<span style={{color:T.yellow}}>Medium</span>,"Draw the six-stage transformer assembly line from memory and label what each stage does in one sentence."],
        [<span style={{color:T.red}}>Hard</span>,"Design an agent that helps students revise for exams. Specify its persona (all five rows), goal, tools, workflow type and why, whether it needs RAG. What breaks if you remove Observation?"],
        [<span style={{color:T.red}}>Discussion</span>,"If Agentic AI can set its own goals, who is responsible when it does something wrong? The AI, the builder, or the user?"],
      ]}
    />
  </div>
);}

function Glossary() {
  const terms = [
    ["AI","Machines doing things that need human-like intelligence"],
    ["GenAI","AI that creates new content — text, images, audio, code"],
    ["LLM","A model trained on huge text data to predict the next token"],
    ["Prompt","The instruction you give an AI"],
    ["Prompt engineering","Writing instructions that reliably get the output you want"],
    ["Multimodality","Handling text, image, audio, video in one system"],
    ["Transformer","The architecture behind LLMs; uses self-attention, works in parallel"],
    ["Self-attention","Every token looking at every other token to decide what matters"],
    ["Token","The small unit text is chopped into — usually a word or word-piece"],
    ["Tokenisation","The process of breaking text into tokens"],
    ["Input embedding","Turning tokens into vectors so the model can process them"],
    ["Vector embedding","A numeric vector representing meaning in high-dimensional space"],
    ["Vector space","The 'map' where similar meanings sit close together"],
    ["Positional encoding","Adding order information so the model knows word positions"],
    ["Multi-head self-attention","Several attention heads in parallel, each catching different relationships"],
    ["Feed-forward network","A network where information flows one way only, no loops"],
    ["Output layer","Projects hidden states to logits over the vocabulary; softmax → probabilities"],
    ["Semantic search","Searching by meaning rather than exact keywords"],
    ["RAG","Retrieve documents first, then generate — the open-book approach"],
    ["Knowledge base","Where the searchable documents live"],
    ["Retrieval strategy","The rules for deciding which and how many documents to fetch"],
    ["Fusion mechanism","Combining retrieved text with the question into one model input"],
    ["Vertex AI RAG Engine","Google Cloud's managed service for building RAG systems"],
    ["Agent","Perceives its environment and acts to achieve goals"],
    ["Agentic AI","Agents with real autonomy — sets goals, adapts, learns"],
    ["Agent persona","A profile defining an agent's identity, role, behaviour, tools and boundaries"],
    ["Tool","A capability the agent can call to reach the outside world"],
    ["ADK","Agent Development Kit — Google's model-agnostic framework"],
    ["BaseAgent","The foundational class every ADK agent inherits from"],
    ["LLM Agent","An agent whose reasoning engine is an LLM"],
    ["Workflow Agent","An agent that orchestrates steps: Sequential, Parallel, or Loop"],
    ["Custom Agent","Your own agent, built by extending BaseAgent"],
  ];
  return (
    <div>
      <P>One line each — for the last page of the notebook.</P>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(340px,1fr))",gap:8}}>
        {terms.map(([term, def], i) => (
          <div key={i} style={{background:T.card,borderRadius:8,padding:"10px 14px",
            border:`1px solid ${T.border}`,display:"flex",gap:10,alignItems:"flex-start"}}>
            <span style={{fontSize:12,fontWeight:700,color:AI,minWidth:140,lineHeight:1.5,flexShrink:0}}>{term}</span>
            <span style={{fontSize:12,color:T.textSec,lineHeight:1.6}}>{def}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── SIDEBAR METADATA ──────────────────────────────────────────────────────────

const SECTIONS = [
  {id:"home", icon:"🏠", title:"Overview",                sub:"12 lessons · 3 parts · course map"},
  {id:"l0",   icon:"✍️", title:"Lesson 0 — Prompts",     sub:"Good vs bad · 5-ingredient recipe"},
  {id:"l1",   icon:"🌳", title:"Lesson 1 — AI Family",   sub:"AI ⊃ GenAI ⊃ LLM"},
  {id:"l2",   icon:"👁️", title:"Lesson 2 — Multimodal",  sub:"Text, image, audio, video in one model"},
  {id:"l3",   icon:"🔄", title:"Lesson 3 — Transformer", sub:"Self-attention · circle vs line"},
  {id:"l4",   icon:"🏭", title:"Lesson 4 — Assembly Line",sub:"6 stages: tokenise → output"},
  {id:"l5",   icon:"📚", title:"Lesson 5 — RAG",         sub:"Open-book exam · library analogy"},
  {id:"l6",   icon:"🤖", title:"Lesson 6 — What is an Agent?", sub:"Perceives · acts · goals · 5 properties"},
  {id:"l7",   icon:"⚡", title:"Lesson 7 — AI vs Agentic",sub:"Execute vs decide"},
  {id:"l8",   icon:"🎭", title:"Lesson 8 — Agent Persona",sub:"Identity, Role, Behaviour, Tools, Boundaries"},
  {id:"l9",   icon:"🛠️", title:"Lesson 9 — Tools & Cycle",sub:"5-step work loop"},
  {id:"l10",  icon:"📦", title:"Lesson 10 — Google ADK",  sub:"LEGO box · BaseAgent · 3 agent types"},
  {id:"l11",  icon:"🎓", title:"Lesson 11 — Resources",   sub:"Activities, confusions, homework, summary"},
  {id:"glos", icon:"📖", title:"Glossary",                sub:"32 terms · one line each"},
];

const sectionComponents = {
  l0:<L0/>, l1:<L1/>, l2:<L2/>, l3:<L3/>, l4:<L4/>,
  l5:<L5/>, l6:<L6/>, l7:<L7/>, l8:<L8/>, l9:<L9/>,
  l10:<L10/>, l11:<L11/>, glos:<Glossary/>,
};

function Overview({ onNav }) {
  const parts = [
    {label:"WARM-UP", color:T.green, items:[{id:"l0",icon:"✍️",title:"Lesson 0","sub":"Good vs Bad Prompts · 5-ingredient recipe"}]},
    {label:"PART 1 — FOUNDATION", color:T.accent, items:[
      {id:"l1",icon:"🌳",title:"Lesson 1","sub":"AI ⊃ GenAI ⊃ LLM"},
      {id:"l2",icon:"👁️",title:"Lesson 2","sub":"Multimodality"},
      {id:"l3",icon:"🔄",title:"Lesson 3","sub":"The Transformer · Self-Attention"},
      {id:"l4",icon:"🏭",title:"Lesson 4","sub":"Inside the Transformer · 6 Stages"},
      {id:"l5",icon:"📚",title:"Lesson 5","sub":"RAG · Open-Book Exam"},
    ]},
    {label:"PART 2 — THE AGENT", color:AI, items:[
      {id:"l6",icon:"🤖",title:"Lesson 6","sub":"What Is an Agent?"},
      {id:"l7",icon:"⚡",title:"Lesson 7","sub":"AI Agents vs Agentic AI"},
      {id:"l8",icon:"🎭",title:"Lesson 8","sub":"Agent Persona"},
      {id:"l9",icon:"🛠️",title:"Lesson 9","sub":"Tools & Work Cycle"},
      {id:"l10",icon:"📦",title:"Lesson 10","sub":"Google ADK · BaseAgent · 3 Types"},
      {id:"l11",icon:"🎓",title:"Lesson 11","sub":"Revision & Activities"},
    ]},
    {label:"REFERENCE", color:T.orange, items:[
      {id:"glos",icon:"📖",title:"Glossary","sub":"32 terms · one line each"},
    ]},
  ];
  return (
    <div>
      <div style={{marginBottom:8,fontSize:11,letterSpacing:2,textTransform:"uppercase",color:AI,fontWeight:700}}>
        AI · AGENTS · GOOGLE ADK
      </div>
      <h1 style={{fontSize:26,fontWeight:800,color:T.textPri,margin:"0 0 10px",lineHeight:1.2}}>
        Agentic AI &amp; Google ADK
      </h1>
      <p style={{fontSize:14,color:T.textSec,lineHeight:1.8,margin:"0 0 6px"}}>
        A teacher's study material — 12 lessons × 40 minutes.
      </p>
      <p style={{fontSize:13,color:T.textMute,lineHeight:1.7,margin:"0 0 20px"}}>
        Each lesson has: <span style={{color:"#A78BFA"}}>🎈 Story</span> (analogy) · <span style={{color:T.accent}}>📖 Definition</span> · <span style={{color:T.green}}>✏️ Board diagram</span> · <span style={{color:T.yellow}}>🎯 Brain check</span>.
        Never give the definition first — give the story, let them feel it, then hand them the words.
      </p>
      {parts.map((part,pi)=>(
        <div key={pi} style={{marginBottom:20}}>
          <div style={{fontSize:10,fontWeight:700,letterSpacing:1.5,color:part.color,
            textTransform:"uppercase",marginBottom:10,borderBottom:`1px solid ${T.border}`,paddingBottom:6}}>
            {part.label}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:10}}>
            {part.items.map(c=>(
              <button key={c.id} onClick={()=>onNav(c.id)}
                style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:10,
                  padding:"12px 14px",textAlign:"left",cursor:"pointer",transition:"border-color .15s"}}
                onMouseEnter={e=>e.currentTarget.style.borderColor=part.color}
                onMouseLeave={e=>e.currentTarget.style.borderColor=T.border}>
                <div style={{fontSize:18,marginBottom:6}}>{c.icon}</div>
                <div style={{fontSize:13,fontWeight:700,color:T.textPri,marginBottom:3}}>{c.title}</div>
                <div style={{fontSize:11.5,color:T.textSec}}>{c.sub}</div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── MAIN EXPORT ───────────────────────────────────────────────────────────────

export default function AgenticADK() {
  const [active, setActive] = useState("home");
  const [visited, setVisited] = useState(new Set(["home"]));
  const mainRef = useRef(null);

  const totalSections = SECTIONS.length - 1;
  const pct = Math.round((visited.size / totalSections) * 100);

  function nav(id) {
    setActive(id);
    setVisited(v => new Set([...v, id]));
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }

  return (
    <div style={{display:"flex",height:"calc(100vh - 70px)",background:T.bg,
      color:T.textPri,fontFamily:"system-ui,sans-serif",overflow:"hidden"}}>

      {/* Sidebar */}
      <div style={{width:240,minWidth:200,borderRight:`1px solid ${T.border}`,
        display:"flex",flexDirection:"column",background:T.surface,overflowY:"auto",flexShrink:0}}>
        <div style={{padding:"14px 14px 0"}}>
          <div style={{fontSize:11,fontWeight:700,letterSpacing:1,textTransform:"uppercase",
            color:AI,marginBottom:10}}>🤖 Agentic AI & ADK</div>
          <div style={{height:4,background:T.border,borderRadius:4,marginBottom:4}}>
            <div style={{height:"100%",borderRadius:4,
              background:`linear-gradient(90deg,${AI},${T.accent})`,
              width:`${pct}%`,transition:"width .3s"}}/>
          </div>
          <div style={{fontSize:10,color:T.textMute,marginBottom:12}}>{pct}% explored</div>
        </div>
        <nav style={{flex:1,padding:"0 8px 16px"}}>
          {SECTIONS.map(s=>{
            const isActive=active===s.id;
            const isDone=visited.has(s.id)&&!isActive;
            return (
              <button key={s.id} onClick={()=>nav(s.id)}
                style={{width:"100%",textAlign:"left",background:isActive?`${AI}15`:"none",
                  border:"none",borderRadius:8,padding:"8px 10px",cursor:"pointer",marginBottom:2,
                  borderLeft:isActive?`3px solid ${AI}`:"3px solid transparent"}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:13}}>{s.icon}</span>
                  <span style={{fontSize:12,fontWeight:isActive?700:500,
                    color:isActive?AI:T.textPri,lineHeight:1.3,flex:1}}>{s.title}</span>
                  {isDone&&<span style={{fontSize:10,color:T.green}}>✓</span>}
                </div>
                <div style={{fontSize:10,color:T.textMute,marginTop:1,paddingLeft:19}}>{s.sub}</div>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Main content */}
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
        {/* Progress bar */}
        <div style={{flexShrink:0,background:T.bg,padding:"8px 40px 10px",
          borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:14}}>{SECTIONS.find(s=>s.id===active)?.icon}</span>
          <span style={{fontSize:13,fontWeight:600,color:T.textPri}}>
            {active==="home"?"Agentic AI & Google ADK":SECTIONS.find(s=>s.id===active)?.title}
          </span>
          <div style={{flex:1,height:3,background:T.border,borderRadius:2}}>
            <div style={{height:"100%",borderRadius:2,
              background:`linear-gradient(90deg,${AI},${T.accent})`,
              width:`${pct}%`,transition:"width .3s"}}/>
          </div>
          <span style={{fontSize:11,color:T.textMute,minWidth:28,textAlign:"right"}}>{pct}%</span>
        </div>

        {/* Scrollable content */}
        <div ref={mainRef} style={{flex:1,overflowY:"auto",padding:"32px 48px"}}>
          {active==="home"
            ? <Overview onNav={nav}/>
            : <div>
                <h2 style={{fontSize:20,fontWeight:700,color:T.textPri,margin:"0 0 20px"}}>
                  {SECTIONS.find(s=>s.id===active)?.title}
                </h2>
                {sectionComponents[active]}
                <div style={{display:"flex",justifyContent:"space-between",marginTop:36,
                  paddingTop:16,borderTop:`1px solid ${T.border}`}}>
                  {(()=>{
                    const idx=SECTIONS.findIndex(s=>s.id===active);
                    const prev=SECTIONS[idx-1];
                    const next=SECTIONS[idx+1];
                    return (<>
                      {prev
                        ? <button onClick={()=>nav(prev.id)} style={{background:T.card,
                            border:`1px solid ${T.border}`,borderRadius:8,padding:"8px 16px",
                            cursor:"pointer",fontSize:12,color:T.textSec,fontFamily:"inherit"}}>
                            ← {prev.title}
                          </button>
                        : <span/>}
                      {next
                        ? <button onClick={()=>nav(next.id)} style={{background:AI,border:"none",
                            borderRadius:8,padding:"8px 16px",cursor:"pointer",fontSize:12,
                            color:"#0B0F1A",fontWeight:700,fontFamily:"inherit"}}>
                            {next.title} →
                          </button>
                        : <span/>}
                    </>);
                  })()}
                </div>
              </div>
          }
        </div>
      </div>
    </div>
  );
}
